import merge from 'lodash/merge';
import type { cancelSubscription as cancelSubscriptionApi } from '@lingoda/api';
import { combineActions, handleActionsImmutably } from '@lingoda/core';
import { createDate } from '@lingoda/dates';
import {
    addSubscriptions,
    confirmCancelSubscription,
    setSubscriptions,
    uncancelSubscription,
} from './actions';
import { SubscriptionFactory, SubscriptionLoadedState } from './models';
import type { Subscription, SubscriptionStatus } from './models';

export interface State {
    items: GenericObject<Subscription>;
    status: SubscriptionLoadedState;
}

type SetSubscriptionsAction = ReturnType<typeof setSubscriptions>;
type AddSubscriptionsAction = ReturnType<typeof addSubscriptions>;
type SuccessAction = ReturnType<typeof uncancelSubscription.success>;
type Success = Unpacked<ReturnType<typeof cancelSubscriptionApi>>;

type Payload = SuccessAction['payload'] & AddSubscriptionsAction['payload'] & Success;

export default handleActionsImmutably<State, Payload>(
    {
        [`${setSubscriptions}`]: (_state, { payload }: SetSubscriptionsAction) => ({
            status: SubscriptionLoadedState.Loaded,
            items: (payload || []).reduce((newState: GenericObject<Subscription>, subscription) => {
                newState[subscription.id] = SubscriptionFactory(subscription);

                return newState;
            }, {}),
        }),
        [`${addSubscriptions}`]: (state, { payload }: AddSubscriptionsAction) => {
            state.status = SubscriptionLoadedState.Loaded;

            (payload || []).forEach((subscription) => {
                state.items[subscription.id] = SubscriptionFactory(subscription);
            });

            return state;
        },
        [`${combineActions(uncancelSubscription.success, confirmCancelSubscription.success)}`]: (
            state,
            { payload }: SuccessAction,
        ) => {
            state.status = SubscriptionLoadedState.Loaded;
            const data = (payload.result as Success).data;
            state.items[data.id] = merge(
                state.items[data.id],
                SubscriptionFactory({
                    ...data,
                    expiresAt: (data.expiresAt && createDate(data.expiresAt)) || undefined,
                    createdAt: createDate(data.createdAt),
                    lastChargedAt:
                        (data.lastChargedAt && createDate(data.lastChargedAt)) || undefined,
                    nextChargeAt: (data.nextChargeAt && createDate(data.nextChargeAt)) || undefined,
                    nextEffectiveChargeDate:
                        (data.nextEffectiveChargeDate &&
                            createDate(data.nextEffectiveChargeDate)) ||
                        undefined,
                    onPaymentRetrySchedule: data.onPaymentRetrySchedule || false,
                    nextPaymentRetryAt:
                        (data.nextPaymentRetryAt && createDate(data.nextPaymentRetryAt)) ||
                        undefined,
                    pausedAt: (data.pausedAt && createDate(data.pausedAt)) || undefined,
                    resumesAt: (data.resumesAt && createDate(data.resumesAt)) || undefined,
                    status: data.status as SubscriptionStatus,
                }),
            );

            return state;
        },
    },
    {
        status: SubscriptionLoadedState.Loading,
        items: {},
    },
);
